// services/api.js - Frontend API Service
const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Convert a blob URL or File to base64 data URL
 */
async function blobToBase64(blobUrl) {
  try {
    // If it's already a base64 data URL, return as-is
    if (blobUrl.startsWith('data:')) {
      return blobUrl;
    }
    
    // If it's not a blob URL, return as-is (might be a server URL)
    if (!blobUrl.startsWith('blob:')) {
      return blobUrl;
    }
    
    // Fetch the blob from the blob URL
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    
    // Convert blob to base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to convert blob to base64:', error);
    return null;
  }
}

/**
 * Convert all photos in form data to base64
 */
async function convertPhotosToBase64(formData) {
  // Deep clone to avoid mutating original
  const converted = JSON.parse(JSON.stringify(formData));
  
  // Convert site condition photos
  if (converted.siteConditions?.photos?.length > 0) {
    console.log(`📷 Converting ${converted.siteConditions.photos.length} site photos to base64...`);
    
    const convertedPhotos = await Promise.all(
      converted.siteConditions.photos.map(async (photo, index) => {
        const imageUrl = photo.previewUrl || photo.url || photo;
        
        // Handle if photo is just a string URL
        if (typeof imageUrl === 'string') {
          const base64 = await blobToBase64(imageUrl);
          if (base64) {
            console.log(`  ✓ Photo ${index + 1} converted (${Math.round(base64.length / 1024)}KB)`);
            return {
              ...photo,
              previewUrl: base64,
              url: base64
            };
          }
          console.warn(`  ✗ Photo ${index + 1} failed to convert`);
          return photo;
        }
        
        // Photo object with previewUrl
        if (photo.previewUrl) {
          const base64 = await blobToBase64(photo.previewUrl);
          if (base64) {
            console.log(`  ✓ Photo ${index + 1} converted (${Math.round(base64.length / 1024)}KB)`);
            return {
              ...photo,
              previewUrl: base64,
              url: base64
            };
          }
        }
        
        console.warn(`  ✗ Photo ${index + 1} has no convertible URL`);
        return photo;
      })
    );
    
    converted.siteConditions.photos = convertedPhotos.filter(p => p.previewUrl || p.url);
    console.log(`📷 Converted ${converted.siteConditions.photos.length} photos successfully`);
  }
  
  // Crew signatures should already be base64 from canvas.toDataURL()
  // but let's make sure they're included properly
  if (converted.crew?.length > 0) {
    converted.crew = converted.crew.map(member => {
      if (member.signature && !member.signature.startsWith('data:')) {
        console.warn(`⚠️ Crew signature for ${member.name} is not base64`);
      }
      return member;
    });
  }
  
  return converted;
}

/**
 * Get form data by short code
 */
export async function getFormData(shortCode) {
  const response = await fetch(`${API_BASE}/api/form/${shortCode}`);
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to load form data');
  }
  
  return response.json();
}

/**
 * Get form status
 */
export async function getFormStatus(shortCode) {
  const response = await fetch(`${API_BASE}/jsa/${shortCode}/status`);
  
  if (!response.ok) {
    throw new Error('Failed to get form status');
  }
  
  return response.json();
}

/**
 * Submit completed form
 * Converts blob URLs to base64 before sending
 */
export async function submitForm(shortCode, formData) {
  // Convert photos from blob URLs to base64 before submission
  console.log('🔄 Preparing form data for submission...');
  const convertedFormData = await convertPhotosToBase64(formData);
  
  // Log payload size for debugging
  const payloadSize = JSON.stringify(convertedFormData).length;
  console.log(`📦 Payload size: ${Math.round(payloadSize / 1024)}KB`);
  
  if (payloadSize > 10 * 1024 * 1024) {
    console.warn('⚠️ Payload exceeds 10MB - submission may fail');
  }
  
  const response = await fetch(`${API_BASE}/api/form/${shortCode}/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(convertedFormData)
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to submit form');
  }
  
  return response.json();
}

/**
 * Upload photos
 */
export async function uploadPhotos(shortCode, files) {
  const formData = new FormData();
  
  files.forEach((file, index) => {
    formData.append(`photo_${index}`, file);
  });
  
  const response = await fetch(`${API_BASE}/api/form/${shortCode}/photos`, {
    method: 'POST',
    body: formData
  });
  
  if (!response.ok) {
    throw new Error('Failed to upload photos');
  }
  
  return response.json();
}

/**
 * Legacy: Get job details by job ID (for backward compatibility)
 */
export async function getJobDetails(jobId, token) {
  // Try to redirect to new short URL system
  const response = await fetch(`${API_BASE}/api/job/${jobId}`, {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  });
  
  if (!response.ok) {
    throw new Error('Failed to load job details');
  }
  
  return response.json();
}

/**
 * Legacy: Submit form by job ID (for backward compatibility)
 */
export async function submitSafetyForm(jobId, formData) {
  // Convert photos before submission
  const convertedFormData = await convertPhotosToBase64(formData);
  
  const response = await fetch(`${API_BASE}/api/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ jobId, ...convertedFormData })
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to submit form');
  }
  
  return response.json();
}

export default {
  getFormData,
  getFormStatus,
  submitForm,
  uploadPhotos,
  getJobDetails,
  submitSafetyForm
};